import React from 'react';
import { Box, Typography, Paper, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import { IChatMessage } from '../../redux/aiChat/aiChat.types';

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAi',
})<{ isAi: boolean }>(({ theme, isAi }) => ({
  display: 'flex',
  flexDirection: isAi ? 'row' : 'row-reverse',
  marginBottom: theme.spacing(0.5),
  alignItems: 'flex-end',
  gap: theme.spacing(1),
}));

const blink = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const Bubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isAi' && prop !== 'isError',
})<{ isAi: boolean; isError?: boolean }>(({ theme, isAi, isError }) => ({
  padding: theme.spacing(1, 1.5),
  borderRadius: isAi 
    ? '20px 20px 20px 4px' 
    : '20px 20px 4px 20px',
  backgroundColor: isError 
    ? theme.palette.error.light 
    : isAi 
      ? theme.palette.grey[100] 
      : '#97c96a',
  color: isError
    ? theme.palette.error.contrastText
    : isAi 
      ? theme.palette.text.primary 
      : '#000',
  maxWidth: '80%',
  boxShadow: isAi 
    ? '0 2px 5px rgba(0,0,0,0.05)' 
    : '0 4px 10px rgba(0,88,190,0.2)',
  position: 'relative',
  border: isError 
    ? `1px solid ${theme.palette.error.main}` 
    : isAi ? `1px solid ${theme.palette.grey[200]}` : 'none',
}));

const StreamingCursor = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: '8px',
  height: '16px',
  backgroundColor: 'currentColor',
  marginLeft: '4px',
  verticalAlign: 'middle',
  animation: `${blink} 1s infinite`,
}));

interface ChatMessageProps {
  message: IChatMessage;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isAi = message.role === 'ai';

  return (
    <MessageContainer isAi={isAi}>
      {isAi && (
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: message.error ? 'error.main' : '#97c96a',
            color: message.error ? 'white' : 'black',
            fontSize: '0.8rem'
          }}
        >
          {message.error ? '!' : 'AI'}
        </Avatar>
      )}
      <Bubble isAi={isAi} isError={message.error} elevation={0}>
        {isAi && !message.error ? (
          <Box
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.7rem',
              lineHeight: 1.6,
              '& p': { margin: '0 0 4px 0' },
              '& p:last-child': { marginBottom: 0 },
              '& h1,& h2,& h3': { fontSize: '0.75rem', fontWeight: 700, margin: '6px 0 2px' },
              '& ul,& ol': { paddingLeft: '1.2em', margin: '2px 0' },
              '& li': { marginBottom: '2px' },
              '& code': {
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0,0,0,0.06)',
                borderRadius: '3px',
                padding: '0 3px',
                fontSize: '0.65rem',
              },
              '& pre': {
                backgroundColor: 'rgba(0,0,0,0.06)',
                borderRadius: '6px',
                padding: '6px 8px',
                overflowX: 'auto',
                margin: '4px 0',
              },
              '& strong': { fontWeight: 700 },
              '& em': { fontStyle: 'italic' },
            }}
          >
            <ReactMarkdown>{message.content}</ReactMarkdown>
            {message.isStreaming && <StreamingCursor />}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ lineHeight: 1.5, fontFamily: 'Inter, sans-serif', fontSize: '0.7rem' }}>
            {message.content}
            {message.isStreaming && <StreamingCursor />}
          </Typography>
        )}
      </Bubble>
    </MessageContainer>
  );
};

export default ChatMessage;
